import { DomEvent } from "leaflet";
import { useEffect, useRef, useState } from "react";
import {
	MapContainer,
	Marker,
	Popup,
	TileLayer,
	useMap,
	ZoomControl,
} from "react-leaflet";
import "leaflet/dist/leaflet.css";

// Icons
import AddCircleIcon from "@mui/icons-material/AddCircle";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import GolfCourseIcon from "@mui/icons-material/GolfCourse";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Fab from "@mui/material/Fab";
import Paper from "@mui/material/Paper";
// MUI Imports
import Skeleton from "@mui/material/Skeleton";
import Snackbar from "@mui/material/Snackbar";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { csrfHeaders } from "./api";
import { PlayedIcon, UnplayedIcon } from "./MapIcons";
import { haversineKm, nearestUnplayed, stalePositionWarning } from "./nearest";

// 1. Map Controller (Kept same)
function MapController({ focus, onFocusComplete, closePopupTrigger }) {
	const map = useMap();
	useEffect(() => {
		if (focus) {
			map.flyTo([focus.lat, focus.lng], focus.zoom, {
				animate: true,
				duration: 1.5,
			});
			onFocusComplete();
		}
	}, [focus, map, onFocusComplete]);
	useEffect(() => {
		if (closePopupTrigger > 0) map.closePopup();
	}, [closePopupTrigger, map]);
	return null;
}

// Rendered inside MapContainer so it can drive the map through useMap(), rather
// than threading a second focus prop through App.js next to the existing one.
function NearestButton({
	courses,
	rounds,
	markerRefs,
	pos,
	setPos,
	index,
	setIndex,
	onMessage,
}) {
	const map = useMap();
	// The moveend handler waiting for the current flyTo to settle. Popup autoPan
	// fights flyTo for the map position if the popup opens mid-flight, so we
	// wait for the animation to actually finish. Tracked so a rapid second tap
	// can cancel the first tap's still-pending handler — otherwise it would
	// open the previous target's popup once the new flight ends.
	const pendingMoveEnd = useRef(null);
	// DOM node of the Fab. MapContainer renders it as a plain child inside
	// Leaflet's own container, and Leaflet binds click/drag/double-tap handlers
	// on that same container — without disableClickPropagation, a double-tap on
	// the button zooms the map and a press-drag pans it out from under the
	// finger.
	const fabRef = useRef(null);
	// Waiting on a GPS fix, which can take the full 10s timeout. React state
	// rather than a ref so the label and the spinner can say so — an unchanged
	// button reads as a broken one.
	const [pending, setPending] = useState(false);

	useEffect(() => {
		if (fabRef.current) DomEvent.disableClickPropagation(fabRef.current);
	}, []);

	useEffect(() => {
		return () => {
			if (pendingMoveEnd.current) map.off("moveend", pendingMoveEnd.current);
		};
	}, [map]);

	// fromIndex is passed explicitly rather than read from `index`, because the
	// caller may have just decided to restart the walk — a setIndex in the same
	// tick would not be visible in this closure.
	const flyToNearest = (position, fromIndex) => {
		const hit = nearestUnplayed(courses, rounds, position, fromIndex);
		if (!hit) {
			onMessage("Du har spilt alle banene 🎉");
			return;
		}
		setIndex(fromIndex + 1);

		if (pendingMoveEnd.current) map.off("moveend", pendingMoveEnd.current);
		const openTargetPopup = () => {
			pendingMoveEnd.current = null;
			markerRefs.current[hit.course.id]?.openPopup();
		};
		pendingMoveEnd.current = openTargetPopup;
		map.once("moveend", openTargetPopup);

		map.flyTo([hit.course.latitude, hit.course.longitude], 12, {
			animate: true,
			duration: 1.5,
		});
	};

	const handleClick = () => {
		// Ask every tap rather than caching the position ourselves. A cache in
		// component state never expires, so a tab left open while you travel keeps
		// answering from where you opened it — observed on a ferry, where the
		// button named a club 66km away because it still held a fix from hours
		// earlier. maximumAge below asks for a recent one, but it is only a
		// request: the same ferry proved the platform will ignore it entirely, so
		// stalePositionWarning checks the timestamp we get back rather than
		// trusting it.
		if (pending) return;
		if (!navigator.geolocation) {
			onMessage("Fant ikke posisjonen din");
			return;
		}
		setPending(true);
		navigator.geolocation.getCurrentPosition(
			({ coords, timestamp }) => {
				setPending(false);
				const stale = stalePositionWarning(timestamp, Date.now());
				if (stale) onMessage(stale);
				const next = { lat: coords.latitude, lng: coords.longitude };
				// Moving invalidates the walk: "5th nearest" from a place you have
				// left ranks against the wrong list. 1km is past GPS jitter while
				// standing still, so repeated taps in one spot keep advancing.
				const moved = !pos || haversineKm(pos, next) > 1;
				setPos(next);
				flyToNearest(next, moved ? 0 : index);
			},
			() => {
				setPending(false);
				onMessage("Fant ikke posisjonen din");
			},
			{ maximumAge: 60000, timeout: 10000 },
		);
	};

	// The label carries the whole explanation: a bare crosshair reads as "centre
	// the map on me", which is the one thing this button does not do. "Neste
	// bane" after the first tap is also the only hint that tapping again walks
	// outward through the nearby clubs.
	let label = index > 0 ? "Neste bane" : "Nærmeste bane";
	if (pending) label = "Finner posisjon…";

	return (
		<Fab
			ref={fabRef}
			variant="extended"
			disabled={pending}
			onClick={handleClick}
			sx={{
				position: "absolute",
				right: 16,
				bottom: 72, // clears the 56px fixed BottomNavigation in App.js
				// Also what Leaflet's own .leaflet-top/.leaflet-bottom control corners
				// (ZoomControl, attribution) use — this only wins that tie via DOM
				// order, since Leaflet builds those before React renders this.
				zIndex: 1000,
				bgcolor: "#2E7D32",
				color: "#fff",
				"&:hover": { bgcolor: "#1b5e20" },
				"&.Mui-disabled": { bgcolor: "#2E7D32", color: "#fff", opacity: 0.7 },
			}}
		>
			{pending ? (
				<CircularProgress size={20} sx={{ mr: 1, color: "#fff" }} />
			) : (
				<GolfCourseIcon sx={{ mr: 1 }} />
			)}
			{label}
		</Fab>
	);
}

function MapView({ user, focus, onFocusComplete }) {
	// --- Data State ---
	const [courses, setCourses] = useState([]);
	const [rounds, setRounds] = useState([]);
	const [loading, setLoading] = useState(true);

	// --- Modal State ---
	const [modalOpen, setModalOpen] = useState(false);
	const [selectedCourse, setSelectedCourse] = useState(null);
	const [score, setScore] = useState("");
	const [date, setDate] = useState(new Date().toISOString().split("T")[0]);
	const [submitting, setSubmitting] = useState(false);

	const [closeTrigger, setCloseTrigger] = useState(0);

	// --- Nearest-course State ---
	const [pos, setPos] = useState(null);
	const [nearestIndex, setNearestIndex] = useState(0);
	const [message, setMessage] = useState("");
	const markerRefs = useRef({});

	// --- Initial Fetch ---
	useEffect(() => {
		setLoading(true);
		const fetchCourses = fetch("/api/courses").then((res) => res.json());

		const fetchRounds = user
			? fetch("/api/rounds").then((res) => res.json())
			: Promise.resolve([]);

		Promise.all([fetchCourses, fetchRounds])
			.then(([coursesData, roundsData]) => {
				setCourses(coursesData || []);
				setRounds(Array.isArray(roundsData) ? roundsData : []);
			})
			.catch((err) => console.error("Failed to load map data", err))
			.finally(() => setLoading(false));
	}, [user]);

	// --- Actions ---
	const openLogRoundModal = (course) => {
		setSelectedCourse(course);
		setScore("");
		setModalOpen(true);
	};

	const handleClose = () => {
		setModalOpen(false);
		setSelectedCourse(null);
		setCloseTrigger((t) => t + 1); // Closes the map popup behind it
	};

	const handleSubmit = async (e) => {
		e.preventDefault(); // Prevent default form submit

		// Validation
		const inputYear = parseInt(date.split("-")[0], 10);
		const currentYear = new Date().getFullYear();

		if (inputYear > currentYear) {
			alert(`Du kan ikke velge årstallet ${inputYear}. Det er i fremtiden!`);
			return;
		}
		if (!score || !selectedCourse) return;

		setSubmitting(true);

		const payload = {
			courseId: selectedCourse.id,
			date: date,
			score: parseInt(score, 10),
		};

		try {
			const res = await fetch("/api/rounds", {
				method: "POST",
				headers: {
					"Content-Type": "application/json",
					...csrfHeaders(),
				},
				body: JSON.stringify(payload),
			});

			if (res.ok) {
				const newRound = await res.json();
				setRounds((prev) => [...prev, newRound]);
				setNearestIndex(0); // the list the index walks just changed
				handleClose();
			} else {
				alert("Kunne ikke lagre runden.");
			}
		} catch (err) {
			console.error(err);
		} finally {
			setSubmitting(false);
		}
	};

	// --- LOADING SKELETON ---
	if (loading) {
		return (
			<Box
				sx={{
					width: "100%",
					height: "100%",
					position: "relative",
					bgcolor: "#e0e0e0",
				}}
			>
				<Skeleton
					variant="rectangular"
					width="100%"
					height="100%"
					animation="wave"
				/>
				{/* Fake UI elements to look like map controls */}
				<Skeleton
					variant="rectangular"
					width={40}
					height={80}
					sx={{ position: "absolute", top: 20, left: 20, borderRadius: 1 }}
				/>
			</Box>
		);
	}

	// --- RENDER MAP ---
	return (
		<>
			<MapContainer
				center={[60.39, 5.32]}
				zoom={10}
				zoomControl={false} // Disable default top-left zoom
				style={{ height: "100%", width: "100%", background: "#cad2d3" }}
			>
				<MapController
					focus={focus}
					onFocusComplete={onFocusComplete}
					closePopupTrigger={closeTrigger}
				/>

				{/* Move Zoom Control to bottom right or keep top left */}
				<ZoomControl position="topleft" />

				<NearestButton
					courses={courses}
					rounds={rounds}
					markerRefs={markerRefs}
					pos={pos}
					setPos={setPos}
					index={nearestIndex}
					setIndex={setNearestIndex}
					onMessage={setMessage}
				/>

				<TileLayer
					url="https://cartodb-basemaps-{s}.global.ssl.fastly.net/light_all/{z}/{x}/{y}.png"
					attribution="&copy; OpenStreetMap &copy; CartoDB"
				/>

				{courses.map((course) => {
					if (!course) return null;

					const courseRounds = rounds.filter((r) => r.courseId === course.id);
					const isPlayed = courseRounds.length > 0;

					const bestScore = isPlayed
						? Math.min(...courseRounds.map((r) => r.score))
						: null;

					return (
						<Marker
							key={course.id}
							ref={(m) => {
								markerRefs.current[course.id] = m;
							}}
							position={[course.latitude, course.longitude]}
							icon={isPlayed ? PlayedIcon : UnplayedIcon}
						>
							{/* --- MUI STYLED POPUP --- */}
							<Popup
								className="mui-popup-override"
								maxWidth={250}
								minWidth={200}
							>
								<Box sx={{ textAlign: "center", p: 1 }}>
									<Typography
										variant="subtitle1"
										sx={{ fontWeight: 700, lineHeight: 1.2, mb: 1 }}
									>
										{course.name || "Ukjent Bane"}
									</Typography>

									{pos && (
										<Typography
											variant="caption"
											sx={{ display: "block", color: "#757575", mb: 1 }}
										>
											{Math.round(
												haversineKm(pos, {
													lat: course.latitude,
													lng: course.longitude,
												}),
											)}{" "}
											km unna
										</Typography>
									)}

									{isPlayed ? (
										<Paper
											elevation={0}
											sx={{
												bgcolor: "#e8f5e9",
												border: "1px solid #c8e6c9",
												borderRadius: 2,
												p: 1.5,
												mb: 2,
											}}
										>
											<Box
												sx={{
													display: "flex",
													alignItems: "center",
													justifyContent: "center",
													gap: 1,
													color: "#2E7D32",
													mb: 0.5,
												}}
											>
												<EmojiEventsIcon fontSize="small" />
												<Typography
													variant="caption"
													fontWeight="bold"
													sx={{ textTransform: "uppercase" }}
												>
													Beste Runde
												</Typography>
											</Box>
											<Typography
												variant="h4"
												sx={{ fontWeight: 800, color: "#2E7D32" }}
											>
												{bestScore}
											</Typography>
										</Paper>
									) : (
										<Typography
											variant="body2"
											sx={{ color: "#757575", fontStyle: "italic", mb: 2 }}
										>
											Ingen runder registrert
										</Typography>
									)}

									<Button
										variant="contained"
										size="small"
										onClick={() =>
											user
												? openLogRoundModal(course)
												: window.location.assign("/login")
										}
										startIcon={!isPlayed && <AddCircleIcon />}
										sx={{
											bgcolor: isPlayed ? "#333" : "#2E7D32",
											width: "100%",
											boxShadow: "none",
											"&:hover": {
												bgcolor: isPlayed ? "#000" : "#1b5e20",
												boxShadow: "none",
											},
										}}
									>
										{isPlayed ? "Ny Runde" : "Registrer"}
									</Button>
								</Box>
							</Popup>
						</Marker>
					);
				})}
			</MapContainer>

			{/* --- MUI DIALOG (MODAL) --- */}
			<Dialog
				open={modalOpen}
				onClose={handleClose}
				fullWidth
				maxWidth="xs"
				slotProps={{
					paper: {
						sx: { borderRadius: 3, p: 1 },
					},
				}}
			>
				<form onSubmit={handleSubmit}>
					<DialogTitle sx={{ pb: 1 }}>
						<Typography variant="h5" fontWeight="700">
							Ny Runde ⛳
						</Typography>
						<Typography variant="body2" color="text.secondary">
							{selectedCourse?.name}
						</Typography>
					</DialogTitle>

					<DialogContent>
						<Box
							sx={{ display: "flex", flexDirection: "column", gap: 2.5, mt: 1 }}
						>
							{/* DATE PICKER (Native type="date" but styled by MUI) */}
							<TextField
								label="Dato"
								type="date"
								fullWidth
								value={date}
								onChange={(e) => setDate(e.target.value)}
								required
								slotProps={{
									inputLabel: {
										shrink: true,
									},
									htmlInput: {
										max: new Date().toISOString().split("T")[0],
										min: "2000-01-01",
									},
								}}
								sx={{
									"& .MuiOutlinedInput-root": { borderRadius: 2 },
								}}
							/>

							{/* SCORE INPUT */}
							<TextField
								label="Score (Slag)"
								type="number"
								fullWidth
								value={score}
								onChange={(e) => setScore(e.target.value)}
								placeholder="f.eks. 82"
								required
								sx={{
									"& .MuiOutlinedInput-root": { borderRadius: 2 },
								}}
							/>
						</Box>
					</DialogContent>

					<DialogActions sx={{ px: 3, pb: 2 }}>
						<Button
							onClick={handleClose}
							color="inherit"
							sx={{ fontWeight: 600 }}
						>
							Avbryt
						</Button>
						<Button
							type="submit"
							variant="contained"
							disabled={submitting}
							sx={{
								bgcolor: "#2E7D32",
								borderRadius: 2,
								px: 3,
								"&:hover": { bgcolor: "#1b5e20" },
							}}
						>
							{submitting ? "Lagrer..." : "Lagre"}
						</Button>
					</DialogActions>
				</form>
			</Dialog>

			<Snackbar
				open={Boolean(message)}
				autoHideDuration={4000}
				onClose={() => setMessage("")}
				message={message}
				anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
				sx={{ bottom: 144 }} // clears the Fab (bottom: 72, 56px tall) instead of covering it
			/>
		</>
	);
}

export default MapView;
